/**
 * /track — Track your request.
 *
 * If buyer already has tokens → fetch /api/quotes and render the list.
 * Otherwise → phone entry screen → OtpSheet → list.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, Loader2, Phone } from "lucide-react";

import { useBuyerAuth } from "../lib/buyer-auth";
import { listMyQuotes, startOtp } from "../lib/api";
import { trackEvent } from "../lib/api";
import OtpSheet from "../components/auth/OtpSheet";
import { toast } from "../components/ui/sonner";

const STATUS_STYLES = {
  submitted: "bg-[#1A1A1A]/5 text-[#1A1A1A]/70",
  under_review: "bg-[#4A6FA5]/10 text-[#4A6FA5]",
  quote_sent: "bg-[#C8A24B]/15 text-[#9A7A2F]",
  accepted: "bg-[#4F8060]/10 text-[#4F8060]",
  declined: "bg-[#B0413E]/10 text-[#B0413E]",
  expired: "bg-[#1A1A1A]/5 text-[#1A1A1A]/50",
};

function formatINR(n) {
  if (n == null) return "–";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n);
}

export default function Track() {
  const { isAuthenticated, buyer } = useBuyerAuth();

  return isAuthenticated ? (
    <QuoteList buyer={buyer} />
  ) : (
    <TrackEntry />
  );
}

// ---------------------------------------------------------------------------
// Entry — phone input → /auth/otp/start → OtpSheet
// ---------------------------------------------------------------------------
function TrackEntry() {
  const [country, setCountry] = useState("+91");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [devOtp, setDevOtp] = useState(null);
  const navigate = useNavigate();

  const isValid = useMemo(() => {
    const digits = phone.replace(/\D/g, "");
    return country === "+91" ? digits.length === 10 : digits.length >= 7 && digits.length <= 15;
  }, [country, phone]);

  const sendOtp = async () => {
    if (!isValid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const data = await startOtp({ phone, country_code: country });
      setDevOtp(data.dev_otp || null);
      setSheetOpen(true);
      trackEvent({
        event: "track_otp_started",
        properties: { entry: "track_page" },
      }).catch(() => {});
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string" ? detail : detail?.message || "Couldn't send code.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      data-testid="track-entry"
      className="mx-auto max-w-md px-5 pb-24 pt-12 sm:px-6 sm:pt-20"
    >
      <p className="text-[10px] uppercase tracking-[0.32em] text-[#C8A24B]">
        Track your request
      </p>
      <h1 className="mt-3 font-display text-4xl text-[#1A1A1A] sm:text-5xl">
        Where's my quote?
      </h1>
      <p className="mt-4 text-sm text-[#1A1A1A]/65 sm:text-base">
        Enter the mobile number you submitted your request with. We'll send a
        one-time code so you can see your conversation with ShutterKART.
      </p>

      <label
        className="mt-10 block text-[11px] uppercase tracking-[0.28em] text-[#1A1A1A]/55"
        htmlFor="track-phone"
      >
        Mobile number
      </label>
      <div className="mt-2 flex items-stretch gap-2 rounded-2xl border border-[#1A1A1A]/15 bg-white px-3 focus-within:border-[#C8A24B]">
        <select
          data-testid="track-country-code"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          className="bg-transparent py-3 pr-2 text-sm text-[#1A1A1A] outline-none"
        >
          <option value="+91">+91</option>
          <option value="+1">+1</option>
          <option value="+44">+44</option>
          <option value="+971">+971</option>
          <option value="+65">+65</option>
        </select>
        <input
          id="track-phone"
          data-testid="track-phone-input"
          inputMode="numeric"
          maxLength={15}
          placeholder="98765 43210"
          value={phone}
          onChange={(e) => setPhone(e.target.value.replace(/[^\d]/g, ""))}
          className="flex-1 bg-transparent py-3 text-base text-[#1A1A1A] outline-none placeholder:text-[#1A1A1A]/35"
        />
      </div>

      {error ? (
        <p data-testid="track-error" className="mt-3 text-xs text-[#B0413E]" role="alert">
          {error}
        </p>
      ) : null}

      <button
        type="button"
        data-testid="track-send-otp"
        onClick={sendOtp}
        disabled={!isValid || submitting}
        className={[
          "mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 text-xs uppercase tracking-[0.22em] transition",
          isValid && !submitting
            ? "bg-[#1A1A1A] text-[#FAF7F2] hover:bg-[#2a2a2a]"
            : "cursor-not-allowed bg-[#1A1A1A]/25 text-[#FAF7F2]",
        ].join(" ")}
      >
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Phone className="h-4 w-4" />}
        Send verification code
      </button>

      <p className="mt-6 text-xs text-[#1A1A1A]/55">
        No account, no signup — just your phone number. Tokens stay on this device.
      </p>

      <p className="mt-12 text-xs text-[#1A1A1A]/55">
        Haven't submitted a request yet?{" "}
        <Link to="/plan" className="font-medium text-[#1A1A1A] underline decoration-[#C8A24B] underline-offset-4">
          Start a quote
        </Link>
      </p>

      <OtpSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        phone={phone}
        countryCode={country}
        devOtp={devOtp}
        resendStart={() => startOtp({ phone, country_code: country })}
        onEditPhone={() => setSheetOpen(false)}
        onVerified={() => {
          setSheetOpen(false);
          toast.success("Verified — loading your quotes.");
          // Will auto-redirect; QuoteList shows on re-render via context.
          navigate("/track", { replace: true });
        }}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Authenticated list view
// ---------------------------------------------------------------------------
function QuoteList({ buyer }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const { signOut } = useBuyerAuth();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    listMyQuotes()
      .then((data) => {
        if (!cancelled) setItems(data || []);
      })
      .catch((e) => {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status === 401) {
          // Force re-auth
          signOut();
          navigate("/track", { replace: true });
          return;
        }
        setError("Couldn't load your quotes. Please try again.");
      });
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section
      data-testid="track-list"
      className="mx-auto max-w-3xl px-5 pb-24 pt-10 sm:px-6 sm:pt-16"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.32em] text-[#C8A24B]">
            Your requests
          </p>
          <h1 className="mt-2 font-display text-3xl text-[#1A1A1A] sm:text-4xl">
            Welcome back
          </h1>
          {buyer?.phone ? (
            <p className="mt-1 text-xs text-[#1A1A1A]/55">{buyer.phone}</p>
          ) : null}
        </div>
        <button
          type="button"
          data-testid="track-signout"
          onClick={async () => {
            await signOut();
            toast.success("Signed out.");
            navigate("/track", { replace: true });
          }}
          className="rounded-full border border-[#1A1A1A]/15 px-4 py-2 text-[10px] uppercase tracking-[0.22em] text-[#1A1A1A]/65 transition hover:border-[#1A1A1A] hover:text-[#1A1A1A]"
        >
          Sign out
        </button>
      </div>

      {error ? (
        <p data-testid="track-list-error" className="mt-8 text-sm text-[#B0413E]">
          {error}
        </p>
      ) : null}

      <div className="mt-8 space-y-3" data-testid="track-list-items">
        {items === null ? (
          [0, 1, 2].map((i) => <div key={i} className="h-28 animate-pulse rounded-2xl bg-[#1A1A1A]/5" />)
        ) : items.length === 0 ? (
          <EmptyState />
        ) : (
          items.map((q) => <QuoteCard key={q.id} quote={q} />)
        )}
      </div>
    </section>
  );
}

function EmptyState() {
  return (
    <div
      data-testid="track-empty"
      className="rounded-3xl border border-dashed border-[#1A1A1A]/15 bg-white/40 px-6 py-12 text-center"
    >
      <p className="font-display text-2xl text-[#1A1A1A]">No requests yet</p>
      <p className="mt-2 text-sm text-[#1A1A1A]/55">
        Start a quote and it will show up here.
      </p>
      <Link
        to="/plan"
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#1A1A1A] px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] text-[#FAF7F2] hover:bg-[#2a2a2a]"
      >
        Plan a shoot
        <ChevronRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  );
}

function QuoteCard({ quote }) {
  const statusPill = STATUS_STYLES[quote.status] || STATUS_STYLES.submitted;
  const formattedDate = new Date(quote.created_at).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  return (
    <Link
      to={`/quotes/${quote.id}`}
      data-testid={`quote-card-${quote.id}`}
      className="group block rounded-3xl border border-[#1A1A1A]/10 bg-white p-5 transition hover:border-[#C8A24B] hover:shadow-sm sm:p-6"
    >
      <div className="flex items-center justify-between gap-3">
        <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.22em] ${statusPill}`}>
          <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-current" />
          {(quote.status || "submitted").replace("_", " ")}
        </span>
        <span className="text-[10px] uppercase tracking-[0.22em] text-[#1A1A1A]/45">
          {quote.short_ref || `SK-${quote.id.replace(/-/g, "").slice(-8).toUpperCase()}`}
        </span>
      </div>
      <h3 className="mt-3 font-display text-2xl capitalize text-[#1A1A1A]">
        {(quote.occasion || "shoot").replaceAll("_", " ")}
      </h3>
      <p className="mt-1 text-sm text-[#1A1A1A]/65">
        {quote.date_summary} · {quote.location_summary}
      </p>
      <div className="mt-3 flex items-end justify-between gap-3 text-xs text-[#1A1A1A]/65">
        <span>
          Submitted <span className="text-[#1A1A1A]">{formattedDate}</span>
        </span>
        {quote.estimated_range_low != null && quote.estimated_range_high != null ? (
          <span className="font-medium text-[#1A1A1A]">
            {formatINR(quote.estimated_range_low)} – {formatINR(quote.estimated_range_high)}
          </span>
        ) : null}
      </div>
    </Link>
  );
}
